import { Button, TextField } from '@ads/components-react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DatePicker } from '@/shared/ui/atoms';

import { useClassModuleFilters } from '../../../hooks/useClassModuleFilters';
import { ClassStudentAutocomplete } from '../LookupAutocomplete/ClassStudentAutocomplete';
import { CompanyAutocomplete } from '../LookupAutocomplete/CompanyAutocomplete';

/**
 * Barra de filtros da listagem de módulos.
 *
 * Mantém o estado pendente localmente e só aplica ao acionar "Buscar"
 * (`updateFilters`). "Limpar" zera todos os campos e o estado aplicado
 * (`resetFilters`). Empresa e Turma usam os autocompletes personalizados do
 * projeto; as datas usam o `DatePicker`.
 */
export function ClassModuleFilterBar() {
  const { t } = useTranslation('classModuleManagement');
  const { updateFilters, resetFilters } = useClassModuleFilters();

  const [description, setDescription] = useState('');
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [companyLabel, setCompanyLabel] = useState('');
  const [classStudentId, setClassStudentId] = useState<number | null>(null);
  const [classStudentLabel, setClassStudentLabel] = useState('');
  const [dateStart, setDateStart] = useState('');
  const [dateFinish, setDateFinish] = useState('');
  // Incrementado ao limpar para remontar os autocompletes — o `Autocomplete` do
  // ADS mantém o texto exibido em estado interno e só o reinicializa ao remontar.
  const [resetNonce, setResetNonce] = useState(0);

  const handleSearch = () => {
    updateFilters({
      description,
      companyId,
      companyLabel,
      classStudentId,
      classStudentLabel,
      dateStart,
      dateFinish,
      page: 1,
    });
  };

  const handleClear = () => {
    setDescription('');
    setCompanyId(null);
    setCompanyLabel('');
    setClassStudentId(null);
    setClassStudentLabel('');
    setDateStart('');
    setDateFinish('');
    setResetNonce(n => n + 1);
    resetFilters();
  };

  return (
    <div className="flex flex-col gap-xxs">
      <div className="grid grid-cols-1 gap-xxxs md:grid-cols-3">
        <TextField
          label={t('list.filters.description')}
          placeholder={t('list.filters.descriptionPlaceholder')}
          value={description}
          size="md"
          fullWidth
          onChange={e => setDescription(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') handleSearch();
          }}
        />

        <CompanyAutocomplete
          key={`company-${resetNonce}`}
          label={t('list.filters.company')}
          placeholder={t('list.filters.companyPlaceholder')}
          value={companyId}
          selectedLabel={companyLabel}
          onChange={(id, label) => {
            setCompanyId(id);
            setCompanyLabel(label);
          }}
        />

        <ClassStudentAutocomplete
          key={`class-${resetNonce}`}
          label={t('list.filters.class')}
          placeholder={t('list.filters.classPlaceholder')}
          value={classStudentId}
          selectedLabel={classStudentLabel}
          onChange={(id, label) => {
            setClassStudentId(id);
            setClassStudentLabel(label);
          }}
        />
      </div>

      <div className="flex flex-col gap-xxxs md:flex-row md:items-end">
        <div className="grid flex-1 grid-cols-1 gap-xxxs md:grid-cols-2">
          <DatePicker
            key={`dateStart-${resetNonce}`}
            label={t('list.filters.dateStart')}
            value={dateStart}
            onChange={setDateStart}
          />
          <DatePicker
            key={`dateFinish-${resetNonce}`}
            label={t('list.filters.dateFinish')}
            value={dateFinish}
            onChange={setDateFinish}
          />
        </div>

        <div className="flex items-center gap-nano">
          <Button
            type="button"
            hierarchy="primary"
            size="md"
            leadingIcon={MagnifyingGlassIcon}
            onClick={handleSearch}
          >
            {t('list.filters.search')}
          </Button>
          <Button type="button" hierarchy="tertiary" size="md" onClick={handleClear}>
            {t('list.filters.clear')}
          </Button>
        </div>
      </div>
    </div>
  );
}
